import React, { MouseEvent, useCallback, useContext, useEffect, useMemo } from 'react';
import util from '../../../common/util';
import { Config } from '../../table/config';
import { RemoveButton } from '../../table/RemoveButton';
import { FormContext, useFieldValue, useSetFieldValue, validateField } from '../../form/DwForm';
import { useIsMounted } from '../../../common/isMounted';
import { EditableDwTable } from '../../table/EditableDwTable';
import { PalletPassportListModel } from '../model/talman/palletPassportModel';
import { PalletPassportField } from './palletPassportField';
import { TalmanModel } from '../model/talman/talmanModel';
import { TalmanField } from './talmanField';
import docs from '../../../api/docs';
import { FileType } from '../../../model/fileType';
import clsx from 'clsx';
import fileUtil from '../../../common/fileUtil';
import { MimeType } from '../../../model/mimeType';
import Scales from '../../form/Scales';
import { RootContext } from '../../layout/ContextWrapper';
import scales from '../../../api/scales';
import { handleApiError } from '../../../api/apiResponse';

import type { SingleValue } from 'react-select/dist/declarations/src/types';
import type { Option } from '../../control/option';
import { DISTRIBUTION_BASED_PALLET_PASSPORT_CREATION_MODE } from '../../../common/const';
import { useParams } from 'react-router-dom';
import { PATH_VARIABLE_SHIPMENT_DETAIL_ID } from '../../../routerPaths';

type Props = {
    id: string;
    editable: boolean;
};

const onDownload = (e: MouseEvent, talmanId: number, model: PalletPassportListModel) => {
    e.preventDefault();
    return docs.palletPassport(talmanId, model, FileType.PDF)
        .then(blob => fileUtil.downloadBlob(blob, `Паспорт_паллеты_${model?.id || fileUtil.formatDateTimeForFileName(new Date())}.${FileType.PDF}`));
};

const onPrint = (e: MouseEvent, talmanId: number, model: PalletPassportListModel) => {
    e.preventDefault();
    return docs.palletPassport(talmanId, model, FileType.PDF)
        .then(blob => fileUtil.printBlob(blob, MimeType.PDF));
};

const onChangeRow = (
    newValue: any,
    rowNum: number,
    row: PalletPassportListModel,
    field: PalletPassportField,
    palletPassports: PalletPassportListModel[],
    onChange: (palletPassports: PalletPassportListModel[]) => void
): Promise<void> => {
    return new Promise<void>((resolve) => {
        if (rowNum === 0) {
            const newPalletPassports = [...palletPassports];
            newPalletPassports
                .filter(pp => !pp[field] || pp[field] === row[field])
                .forEach(pp => {
                    pp[field] = newValue;
                });
            onChange(newPalletPassports);
        }
        resolve();
    });
};

const useFormConfig = (
    id: string,
    talmanId: number,
    editable: boolean,
    palletPassportsRemaining: number,
    palletPassports: PalletPassportListModel[],
    onChange: (palletPassports: PalletPassportListModel[]) => void,
    onAdd: (e: React.UIEvent<HTMLElement> | React.KeyboardEvent) => void,
    onDelete: (e: MouseEvent, row: PalletPassportListModel, index: number) => void,
    handleScalesChange: (newValue: SingleValue<Option>) => void,
    handleExternalWeight: (e: MouseEvent, row: PalletPassportListModel, rowNum: number) => void
) =>
    useMemo(
        () =>
            Config.builder<PalletPassportListModel>()
                .column((builder) => builder.position('#').cls('id'))
                .column((builder) => builder.integer('Количество мест', PalletPassportField.BOXES).editable(() => editable).cls('number'))
                .column((builder) => builder.decimal('Фактический вес брутто, кг', PalletPassportField.GROSS_WEIGHT).editable(() => editable).cls('number'))
                .column((builder) =>
                    builder
                        .decimal('Вес пустой коробки', PalletPassportField.BOX_WEIGHT)
                        .editable(() => editable)
                        .onChangeRow((newValue, rowNum, row) =>
                            onChangeRow(newValue, rowNum, row, PalletPassportField.BOX_WEIGHT, palletPassports, onChange))
                        .cls('number')
                )
                .column((builder) =>
                    builder
                        .decimal('Вес пустого поддона + скрепл. материалов', PalletPassportField.PALLET_WEIGHT)
                        .editable(() => editable)
                        .onChangeRow((newValue, rowNum, row) =>
                            onChangeRow(newValue, rowNum, row, PalletPassportField.PALLET_WEIGHT, palletPassports, onChange))
                        .cls('number')
                )
                .column((builder) => builder.label('Фактический вес нетто, кг').getter(calcNetWeight).cls('number'))
                .column((builder) =>
                    builder
                        .label('Действия')
                        .getter((model) => {
                            return (
                                <>
                                    <button
                                        className='btn btn-sm btn-primary'
                                        title='Получить вес'
                                        disabled={!editable}
                                        onClick={(e) =>
                                            editable
                                                ? handleExternalWeight(
                                                      e,
                                                      model,
                                                      palletPassports.indexOf(model)
                                                  )
                                                : e.preventDefault()
                                        }
                                    >
                                        <i className='icon fa fa-balance-scale' />
                                    </button>
                                    &nbsp;
                                    <button
                                        className='btn btn-sm btn-primary'
                                        title='Напечатать документ'
                                        onClick={(e) => onPrint(e, talmanId, model)}
                                    >
                                        <i className='icon fa fa-print' />
                                    </button>
                                    &nbsp;
                                    <button
                                        className='btn btn-sm btn-primary'
                                        title='Скачать документ'
                                        onClick={(e) => onDownload(e, talmanId, model)}
                                    >
                                        <i className='icon fa fa-download' />
                                    </button>
                                </>
                            );
                        })
                        .cls('text-center actions')
                        .width('20px')
                )
                .column((builder) =>
                    builder
                        .getter((model, index) => {
                            return (
                                <RemoveButton
                                    disabled={!editable}
                                    onClick={(e) => editable ? onDelete(e, model, index!) : e.preventDefault()}
                                />
                            );
                        })
                        .cls('text-center actions')
                        .width('20px')
                )
                .id(id)
                .captionControl(() => (
                    <div className='d-flex flex-row'>
                        <div className='mr-4'>
                            <Scales id={TalmanField.SCALES} onChange={handleScalesChange} />
                        </div>
                        &nbsp;
                        <button
                            className={clsx('btn btn-outline-primary btn-sm', {
                                disabled: DISTRIBUTION_BASED_PALLET_PASSPORT_CREATION_MODE
                                        ? palletPassportsRemaining <= 0 || !editable
                                        : !editable,
                            })}
                            onClick={(e) => {
                                if (DISTRIBUTION_BASED_PALLET_PASSPORT_CREATION_MODE) {
                                    return palletPassportsRemaining > 0 && editable ? onAdd(e) : e.preventDefault();
                                }
                                return editable ? onAdd(e) : e.preventDefault();
                            }}
                        >
                            добавить
                        </button>
                    </div>
                ))
                .withAdd(!editable)
                .build(),
        [
            id,
            talmanId,
            editable,
            palletPassportsRemaining,
            palletPassports,
            onChange,
            onAdd,
            onDelete,
            handleScalesChange,
            handleExternalWeight,
        ]
    );

const calcNetWeight = (model: PalletPassportListModel) => {
    const { grossWeight, boxes, boxWeight, palletWeight } = model;
    if (grossWeight > 0) {
        let netWeight = grossWeight;
        if (boxes > 0 && boxWeight > 0) {
            netWeight -= boxes * boxWeight;
        }
        if (palletWeight > 0) {
            netWeight -= palletWeight;
        }
        return netWeight > 0 ? parseFloat(netWeight.toFixed(2)) : 0;
    }
    return 0;
};

const calcTotalNetWeight = (palletPassports: PalletPassportListModel[]) => palletPassports.reduce(
    (total, passport) => total + Number(calcNetWeight(passport)), 0
).toFixed(2);

export const PalletPassportTable: React.FC<Props> = ({ id, editable }) => {
    const context = useContext(FormContext);
    const model = context.state.model as TalmanModel;
    const talmanId = util.toNumber(useParams()[PATH_VARIABLE_SHIPMENT_DETAIL_ID]!)!;
    const setValue = useSetFieldValue();
    const fieldConfig = context.state.config.field(id);
    const palletPassportsDataValue = fieldConfig.getter(model);
    const palletPassportsData: PalletPassportListModel[] = useMemo(() => palletPassportsDataValue ?? [], [palletPassportsDataValue]);
    const {
        state: { uiState },
        setUiState,
    } = useContext(RootContext);

    const clientAssortmentBoxes = useFieldValue(TalmanField.CLIENT_ASSORTMENT_BOXES);
    const maxPalletPassports = useFieldValue(TalmanField.MAX_PALLET_PASSPORTS);
    const palletPassportsRemaining = useMemo(() => (maxPalletPassports ?? 0) - palletPassportsData.length, [palletPassportsData, maxPalletPassports]);
    const currentScales = useFieldValue(TalmanField.SCALES);

    const isMounted = useIsMounted();
    useEffect(() => {
        if (isMounted) {
            validateField(context, fieldConfig, model).then();
        }
    }, [palletPassportsData]);

    useEffect(() => {
        if (isMounted && uiState.currentScales?.value > 0) {
            const { currentScales } = uiState;
            if (model[TalmanField.SCALES] !== currentScales) {
                setValue(TalmanField.SCALES, currentScales);
            }
        }
    }, [isMounted, setValue, uiState, model]);

    const onChange = useCallback((palletPassports: PalletPassportListModel[]) => setValue(id, palletPassports), [id, setValue]);

    const onDelete = (e: MouseEvent, row: PalletPassportListModel, index: number) => {
        e.preventDefault();
        const palletPassports = [...palletPassportsData];
        palletPassports.splice(index, 1);
        onChange(palletPassports);
    };

    const onAdd = useCallback((e: React.UIEvent<HTMLElement> | React.KeyboardEvent): void => {
        e.preventDefault();
        const { boxWeight: defaultBoxWeight, palletWeight: defaultPalletWeight } = palletPassportsData[0] || {};
        const palletPassport: Partial<PalletPassportListModel> = {
            boxes: clientAssortmentBoxes,
            boxWeight: defaultBoxWeight,
            palletWeight: defaultPalletWeight,
        };
        onChange([...palletPassportsData, palletPassport as PalletPassportListModel]);
    }, [palletPassportsData, onChange, clientAssortmentBoxes]);

    const handleScalesChange = useCallback(
        (newValue: SingleValue<Option>) => {
            if (!newValue) return;

            const shouldUpdateScales =
                !currentScales ||
                currentScales?.value < 0 ||
                window.confirm('Вы уверены что хотите сменить весы?');

            if (shouldUpdateScales) {
                setUiState({
                    ...uiState,
                    currentScales: newValue,
                });
            }
        },
        [currentScales, setUiState, uiState]
    );

    const handleExternalWeight = useCallback(
        async (e: MouseEvent, row: PalletPassportListModel, rowNum: number) => {
            e.preventDefault();

            if (!currentScales?.value || currentScales.value < 1) {
                return alert('Весы не выбраны');
            }

            const { uri, token } = await scales.get(currentScales.value);
            return await scales.fetchExternalWeight({ uri, token }).then(({ message, error }) => {
                if (error) {
                    return handleApiError(error);
                }

                const updatedRow = { ...row, grossWeight: Number(message) };
                const updatedPalletPassports = palletPassportsData.map((item, idx) =>
                    idx === rowNum ? updatedRow : item
                );

                onChange(updatedPalletPassports);
            });
        },
        [currentScales?.value, onChange, palletPassportsData]
    );

    const config = useFormConfig(
        id,
        talmanId,
        editable,
        palletPassportsRemaining,
        palletPassportsData,
        onChange,
        onAdd,
        onDelete,
        handleScalesChange,
        handleExternalWeight
    );

    return (
        <div className='pallet-passports'>
            <EditableDwTable config={config} header={'Паспорта паллет'}>
                <tr className={'summary-row'}>
                    <td className='text-center'>Итого</td>
                    <td>{util.sumOverObjectProps(palletPassportsData, PalletPassportField.BOXES)}</td>
                    <td>{util.sumOverObjectProps(palletPassportsData, PalletPassportField.GROSS_WEIGHT, 2)}</td>
                    <td>{util.sumOverObjectProps(palletPassportsData, PalletPassportField.BOX_WEIGHT, 2)}</td>
                    <td>{util.sumOverObjectProps(palletPassportsData, PalletPassportField.PALLET_WEIGHT, 2)}</td>
                    <td>{parseFloat(calcTotalNetWeight(palletPassportsData))}</td>
                    <td />
                </tr>
            </EditableDwTable>
        </div>
    );
};
